import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { useDebounce } from "stimulus-use"
import { computePosition, flip, shift, offset, size } from "@floating-ui/dom"

const NUMERIC = /^\d+$/

export default class extends Controller {
  static targets = [
    "input",
    "suggestions",
    "item"
  ]

  static values = {
    indexUrl: String,
    keywordParam: String
  }

  static debounces = [ "submit" ]

  connect() {
    useDebounce(this, { wait: 200 })
  }

  stopClick(event) {
    event.stopPropagation()
  }

  clear(event) {
    event.preventDefault()
    event.stopPropagation()

    this.clearInput()
  }

  clearInput() {
    this.inputTarget.value = ""
    this.suggestionsTarget.innerHTML = ""
  }

  // Enter without picking a suggestion opens the building whose ID matches the query,
  // and otherwise falls back to the buildings list filtered by the query as a keyword.
  async confirm(event) {
    event.preventDefault()
    event.stopPropagation()

    const query = this.inputTarget.value.trim()

    if (query === "") return

    if (!NUMERIC.test(query)) {
      Turbo.visit(this.#indexUrl(query))
      return
    }

    await this.#refreshSuggestions()
    this.itemTargets.find((item) => item.dataset.buildingId === query)?.click()
  }

  hideSuggestions(event) {
    if (!this.suggestionsTarget.matches(":popover-open")) return

    if (!(this.suggestionsTarget.contains(event.relatedTarget)) || event.relatedTarget === null) {
      this.suggestionsTarget.hidePopover()
    }
  }

  async showSuggestions() {
    await new Promise(requestAnimationFrame)

    if (!this.hasInputTarget || !this.hasSuggestionsTarget) return
    if (this.inputTarget !== document.activeElement) return
    if (this.suggestionsTarget.matches(":popover-open")) return

    this.suggestionsTarget.showPopover()
    this.#updatePosition()
  }

  submit() {
    this.element.requestSubmit()
  }

  #indexUrl(query) {
    const url = new URL(this.indexUrlValue, window.location.origin)
    url.searchParams.set(this.keywordParamValue, query)

    return url.toString()
  }

  // Suggestions are debounced, so they can still be stale when a value is pasted and
  // confirmed right away. Re-request them and wait for the frame before reading the items.
  #refreshSuggestions() {
    const loaded = new Promise((resolve) => {
      this.suggestionsTarget.addEventListener("turbo:frame-load", resolve, { once: true })
    })

    this.element.requestSubmit()

    return loaded
  }

  async #updatePosition() {
    await new Promise(requestAnimationFrame)

    const { x, y } = await computePosition(this.inputTarget, this.suggestionsTarget, {
      placement: "bottom-start",
      strategy: "fixed",
      middleware: [
        offset(8),
        flip(),
        shift({ padding: 8 }),
        size({
          apply({ rects, elements }) {
            elements.floating.style.width = `${rects.reference.width}px`
          }
        })
      ]
    })

    Object.assign(this.suggestionsTarget.style, {
      position: "fixed",
      left: `${x}px`,
      top: `${y}px`,
    })
  }
}
